// src/pages/partners/partners-logged-in/routes/dashboard/index.tsx
// Left slide-in column now scrollable; close button removed.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import { fetchAllClientsWithWorkouts } from "../../../../../redux/thunks/thunk-partners";
import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import MiniChat from "./mini-chat";
import { setDashboardClient } from "../../../../../redux/slices/partnersSlice";
import { Meal, User, Workout } from "../../../../../models/types";
import { ClientOverview } from "./first-col";
import { OpenInNewRounded } from "@mui/icons-material";

type DashboardProps = {
  onNavigateToClient?: (clientUserId: string) => void;
};

type AttentionSeverity = "critical" | "moderate" | "watch";
type AttentionFilter = "all" | AttentionSeverity;

const ATTENTION_FILTER_OPTIONS: Array<{
  value: AttentionFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "critical", label: "Critical" },
  { value: "moderate", label: "Moderate" },
  { value: "watch", label: "Watch" },
];

const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

// helpers
const today = () => new Date();
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
};
function inRange(dateISO?: string, start?: Date, end?: Date) {
  if (!dateISO) return false;
  const d = new Date(dateISO);
  return (!start || d >= start) && (!end || d <= end);
}
function workoutsPerWeek(workouts: Workout[], windowDays: number = 14) {
  const start = daysAgo(windowDays - 1);
  const end = today();
  const count = (workouts ?? []).filter((w) =>
    inRange(w.dayOfExecution, start, end)
  ).length;
  return (count / windowDays) * 7;
}
type DayAgg = {
  calories: number;
  protein: number;
  hasMeals: boolean;
};
function mealAggLastNDays(meals: Meal[] | undefined, nDays = 7) {
  const start = daysAgo(nDays - 1);
  const agg: Record<string, DayAgg> = {};
  for (let i = 0; i < nDays; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = d.toISOString().slice(0, 10);
    agg[key] = { calories: 0, protein: 0, hasMeals: false };
  }
  (meals ?? []).forEach((m) => {
    if (!inRange(m.dateTime, daysAgo(nDays - 1), today())) return;
    const key = new Date(m.dateTime).toISOString().slice(0, 10);
    if (!agg[key]) agg[key] = { calories: 0, protein: 0, hasMeals: false };
    agg[key].calories += m.calories ?? 0;
    agg[key].protein += m.protein ?? 0;
    agg[key].hasMeals = true;
  });
  return agg;
}
function trackingConsistencyPct(meals: Meal[] | undefined, nDays = 7) {
  const agg = mealAggLastNDays(meals, nDays);
  const vals = Object.values(agg);
  const tracked = vals.filter((d) => d.hasMeals).length;
  return (tracked / nDays) * 100;
}
function goalMissRate(
  user: User,
  nDays = 7
): {
  missPct: number | null;
  basis: "calories" | "protein";
  trackedDays: number;
  daysHit: number;
  hasGoal: boolean;
} {
  const agg = mealAggLastNDays(user.meals, nDays);
  const useCalories =
    user.goals?.trainingGoal === "weight loss" ||
    user.goals?.trainingGoal === "maintain";
  const basis: "calories" | "protein" = useCalories ? "calories" : "protein";
  const goal =
    basis === "calories" ? user.goals?.caloriesGoal ?? 0 : user.goals?.proteinGoal ?? 0;
  const hasGoal = goal > 0;

  let tracked = 0;
  let misses = 0;
  let hits = 0;

  if (!hasGoal) {
    return {
      missPct: null,
      basis,
      trackedDays: 0,
      daysHit: 0,
      hasGoal: false,
    };
  }

  for (const d of Object.values(agg)) {
    if (!d.hasMeals) continue;
    if (basis === "calories") {
      const lower = goal * 0.9;
      const upper = goal * 1.1;
      const hit = d.calories >= lower && d.calories <= upper;
      tracked++;
      if (!hit) misses++;
      else hits++;
    } else {
      const hit = d.protein >= goal;
      tracked++;
      if (!hit) misses++;
      else hits++;
    }
  }
  return {
    missPct: tracked > 0 ? (misses / tracked) * 100 : null,
    basis,
    trackedDays: tracked,
    daysHit: hits,
    hasGoal: true,
  };
}

// ───────────────────────────────────────────────────────────────────────────────
// Clickable Ranked list — clean, borderless dark-mode design
// ───────────────────────────────────────────────────────────────────────────────
function RankedList({
  items,
  emptyText,
  onSelect,
  selectedId,
  selectedClient,
  onNavigateToClient,
  darkMode,
}: {
  items: Array<{
    id: string;
    rank: number;
    primary: string;
    secondary?: React.ReactNode;
    right?: React.ReactNode;
  }>;
  emptyText: string;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  selectedClient?: User | null;
  onNavigateToClient?: (userId: string) => void;
  darkMode: boolean;
}) {
  if (!items.length) {
    return <Typography color="text.secondary">{emptyText}</Typography>;
  }

  return (
    <>
      <List dense>
        {items.map((it) => {
          const isSelected = selectedId === it.id;
          return (
            <ListItem key={it.id} disablePadding sx={{ mb: 0.5 , pb:1}}>
              <ListItemButton
                selected={isSelected}
                onClick={() => onSelect?.(it.id)}
                sx={{
                  borderRadius: 2.5,
                  px: 1.5,
                  py: 1,
                  transition: "background 0.2s ease",
                  backgroundColor: darkMode
                    ? "rgba(255,255,255,0.03)" // Base background for distinction
                    : "white",
                  boxShadow: darkMode ? "none" : "0 1px 3px rgba(0,0,0,0.05)",
                  border: darkMode
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "1px solid rgba(0,0,0,0.04)",
                  "&:hover": {
                    backgroundColor: darkMode
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.02)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: darkMode
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                    "&:hover": {
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.10)"
                        : "rgba(0,0,0,0.08)",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: "100%" }}
                      gap={1}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            flexShrink: 0,
                            backgroundColor: darkMode
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.06)",
                            color: darkMode ? "#A0A0A5" : "#6E6E73",
                            border: darkMode
                              ? "1px solid rgba(255,255,255,0.10)"
                              : "1px solid rgba(0,0,0,0.08)",
                          }}
                        >
                          {it.rank}
                        </Box>
                        <Typography
                          fontWeight={500}
                          fontSize="0.9rem"
                          sx={{
                            color: darkMode ? "#E8E8ED" : "#1D1D1F",
                          }}
                        >
                          {it.primary}
                        </Typography>
                      </Box>
                      {it.right}
                    </Stack>
                  }
                  secondary={
                    it.secondary ? (
                      <Stack
                        direction="column"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          pl: "42px",
                          pr: 0.5,
                          boxSizing: "border-box",
                        }}
                        gap={0.5}
                      >
                        {it.secondary}
                      </Stack>
                    ) : undefined
                  }
                  secondaryTypographyProps={{
                    component: "div",
                    sx: { width: "100%" },
                  }}
                />

                {selectedClient && onNavigateToClient && isSelected && (
                  <Box sx={{ pt: 0, pl: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        onNavigateToClient(selectedClient.userId)
                      }
                      startIcon={<OpenInNewRounded />}
                      sx={{ borderRadius: 2 }}
                    >
                      Open
                    </Button>
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────────────────
export default function Dashboard({ onNavigateToClient }: DashboardProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [idToken, setIdToken] = useState<string>("");
  const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>("all");

  const darkMode = Boolean(
    useSelector((state: RootState) => state.partners.darkMode)
  );
  const partnerId =
    useSelector(
      (s: RootState) => s.partners.partnerData?.partnerData?.userId
    ) ?? null;

  const clientsState = useSelector(
    (s: RootState) => s.partners.clientsWithWorkouts
  ) as User[] | null;

  const cwwStatus = useSelector(
    (s: RootState) => s.partners.clientsWithWorkoutsStatus
  );

  const selectedClient = useSelector(
    (s: RootState) => s.partners.dashboardClientSelected
  ) as User | null;

  // Cognito token
  useEffect(() => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) return;
    currentUser.getSession((err: any, session: CognitoUserSession | null) => {
      if (err) {
        console.error("Error retrieving session:", err);
        return;
      }
      if (session && session.isValid()) {
        setIdToken(session.getIdToken().getJwtToken());
      }
    });
  }, []);

  // Fetch once if needed
  const hasClients = (clientsState ?? []).length > 0;
  useEffect(() => {
    if (!idToken || !partnerId) return;
    if (hasClients) return;
    dispatch(fetchAllClientsWithWorkouts({ idToken, partnerId }));
  }, [dispatch, idToken, partnerId, hasClients]);

  // Clear selection if missing
  useEffect(() => {
    if (!selectedClient) return;
    const exists = (clientsState ?? []).some(
      (c) => c.userId === selectedClient.userId
    );
    if (!exists) {
      dispatch(setDashboardClient(null));
    }
  }, [clientsState, selectedClient, dispatch]);

  // Refresh
  const handleRefresh = useCallback(() => {
    if (idToken && partnerId) {
      dispatch(fetchAllClientsWithWorkouts({ idToken, partnerId }));
    }
  }, [dispatch, idToken, partnerId]);

  const loading = cwwStatus === "loading";
  const clients = useMemo<User[]>(() => clientsState ?? [], [clientsState]);
  const numClients = clients.length;

  // Attention ranking
  type AttentionRow = {
    id: string;
    name: string;
    rank: number;
    wpw: number;
    dietPct: number;
    mealTrackedDays: number;
    missPct: number | null;
    goalBasis: "calories" | "protein";
    goalTrackedDays: number;
    hasGoal: boolean;
    reasons: string[];
    severity: AttentionSeverity;
    score: number;
  };

  const attentionRank = useMemo<AttentionRow[]>(() => {
    if (!clients.length) return [];
    return clients
      .map((u) => {
        const wpw = workoutsPerWeek(u.workouts ?? [], 14);
        const dietPct = trackingConsistencyPct(u.meals ?? [], 7);
        const mealTrackedDays = Math.round((dietPct / 100) * 7);
        const goalStatus = goalMissRate(u, 7);
        const missPct = goalStatus.missPct;

        const wpwPenalty = Math.max(0, 2 - wpw) / 2;
        const dietLack = (100 - dietPct) / 100;
        const missPenalty =
          missPct === null
            ? goalStatus.hasGoal
              ? 0.2
              : 0.1
            : missPct / 100;
        const baseScore = 0.5 * wpwPenalty + 0.3 * dietLack + 0.2 * missPenalty;

        // Down-weight sparse datasets so "missing logs" does not outrank confirmed misses.
        const observedSignals = mealTrackedDays + goalStatus.trackedDays;
        const confidence = Math.min(1, observedSignals / 10);
        const confidenceWeight = 0.55 + confidence * 0.45;
        let score = Math.min(
          1,
          baseScore * confidenceWeight + (mealTrackedDays < 2 ? 0.08 : 0)
        );
        const noNutritionTracking =
          mealTrackedDays === 0 && goalStatus.trackedDays === 0;
        const veryLowWorkoutData = wpw < 1;
        if (noNutritionTracking && veryLowWorkoutData) {
          // Escalate true "silent client" profiles to top triage.
          score = Math.max(score, 0.72);
        } else if (noNutritionTracking) {
          score = Math.max(score, 0.5);
        }

        const reasons: string[] = [];
        if (wpw < 1.0) reasons.push("Very low workout activity");
        else if (wpw < 2.0) reasons.push("Low workout consistency");
        if (dietPct === 0) reasons.push("No recent food logs");
        else if (dietPct < 50) reasons.push("Inconsistent food tracking");
        if (noNutritionTracking) {
          reasons.push("No tracked nutrition data (7d)");
        }
        if (!goalStatus.hasGoal) {
          reasons.push(
            `${goalStatus.basis === "calories" ? "Calorie" : "Protein"} goal missing`
          );
        } else if (missPct === null) {
          reasons.push("Not enough goal evidence");
        } else if (missPct > 60) {
          reasons.push(`High ${goalStatus.basis} misses`);
        } else if (missPct > 40) {
          reasons.push(`Some ${goalStatus.basis} misses`);
        }

        let severity: AttentionSeverity =
          score >= 0.66 ? "critical" : score >= 0.45 ? "moderate" : "watch";
        if (noNutritionTracking && veryLowWorkoutData) {
          severity = "critical";
        }

        return {
          id: u.userId,
          name: u.name || u.email || u.userId,
          rank: 0,
          wpw,
          dietPct,
          mealTrackedDays,
          missPct,
          goalBasis: goalStatus.basis,
          goalTrackedDays: goalStatus.trackedDays,
          hasGoal: goalStatus.hasGoal,
          reasons: reasons.length ? reasons : ["Needs check-in"],
          severity,
          score,
        };
      })
      .sort((a, b) => b.score - a.score)
      .map((r, idx) => ({ ...r, rank: idx + 1 }));
  }, [clients]);

  const attentionCounts = useMemo(
    () =>
      attentionRank.reduce(
        (acc, row) => {
          acc.all += 1;
          acc[row.severity] += 1;
          return acc;
        },
        { all: 0, critical: 0, moderate: 0, watch: 0 } as Record<
          AttentionFilter,
          number
        >
      ),
    [attentionRank]
  );

  const filteredAttention = useMemo(
    () =>
      attentionFilter === "all"
        ? attentionRank
        : attentionRank.filter((row) => row.severity === attentionFilter),
    [attentionFilter, attentionRank]
  );

  const attentionItems = useMemo(
    () =>
      filteredAttention.map((r) => {
        const severityColor =
          r.severity === "critical"
            ? darkMode
              ? "#ff8a80"
              : "#d32f2f"
            : r.severity === "moderate"
              ? darkMode
                ? "#ffcc80"
                : "#ed6c02"
              : darkMode
                ? "#cfd8dc"
                : "#455a64";
        const goalMetricLabel = !r.hasGoal
          ? `${r.goalBasis === "calories" ? "Calorie" : "Protein"} goal not set`
          : r.missPct === null
            ? `${r.goalTrackedDays}/7 goal days tracked`
            : `${Math.round(r.missPct)}% ${r.goalBasis} misses`;

        return {
          id: r.id,
          rank: r.rank,
          primary: r.name,
          right: (
            <Chip
              size="small"
              label={r.severity}
              sx={{
                height: 20,
                textTransform: "capitalize",
                fontSize: "0.65rem",
                fontWeight: 700,
                color: severityColor,
                bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                border: `1px solid ${severityColor}55`,
              }}
            />
          ),
          secondary: (
            <Stack
              sx={{
                width: "100%",
                mt: 1,
              }}
              gap={0.75}
            >
              <Typography
                variant="caption"
                sx={{
                  color: severityColor,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "currentColor",
                    opacity: 0.8,
                  }}
                />
                {r.reasons.join(" • ")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                <Chip
                  size="small"
                  label={`${r.wpw.toFixed(1)} workouts/wk`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`${Math.round(r.dietPct)}% meal tracking`}
                  variant="outlined"
                />
                <Chip size="small" label={goalMetricLabel} variant="outlined" />
              </Stack>
            </Stack>
          ),
        };
      }),
    [darkMode, filteredAttention]
  );

  const totalAttentionItems = attentionRank.length;

  const onSelectClient = (id: string) => {
    const user = clients.find((c) => c.userId === id) || null;
    const next = selectedClient?.userId === user?.userId ? null : user;
    dispatch(setDashboardClient(next));
  };

  const sliderOpen = Boolean(selectedClient);

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        px: 1,
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gridTemplateColumns: "1fr",
      }}
    >
      {/* THREE AREAS IN ONE ROW:
          [Left slide-in client detail] [Needs Attention] [MiniChat]
          Left column width animates 0 -> 380/420px, pushing the rest to the right. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            // first column wider now
            xs: "minmax(280px, 1fr) 1fr",
            md: sliderOpen ? "360px .25fr 1fr" : "360px 0fr 1fr",
            lg: sliderOpen ? "420px .25fr 1fr" : "420px 0fr 1fr",
          },
          gap: 1,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* left — Needs Attention */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
          }}
        >
          <CardHeader
            title={
              <Stack gap={1}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                      fontSize={18}
                      fontWeight={500}
                      lineHeight={1.2}
                      color={darkMode ? "#B0B0B5" : "#6E6E73"}
                      letterSpacing={0.2}
                    >
                      Needs attention
                    </Typography>
                    <Chip
                      size="small"
                      label={`${attentionCounts[attentionFilter]} shown / ${totalAttentionItems}`}
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: darkMode ? "#E8E8ED" : "#2D2D31",
                      }}
                    />
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={handleRefresh}
                    sx={{ p: 0.5 }}
                    aria-label="Refresh needs attention list"
                    disabled={loading}
                  >
                    <RefreshIcon
                      sx={{
                        fontSize: 16,
                        color: darkMode ? "#8E8E93" : "#8E8E93",
                        animation: loading
                          ? "attention-spin 900ms linear infinite"
                          : "none",
                        "@keyframes attention-spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </IconButton>
                </Box>
                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                  {ATTENTION_FILTER_OPTIONS.map((option) => {
                    const isActive = option.value === attentionFilter;
                    return (
                      <Chip
                        key={option.value}
                        size="small"
                        clickable
                        onClick={() => setAttentionFilter(option.value)}
                        label={`${option.label} (${attentionCounts[option.value]})`}
                        aria-label={`Filter needs attention by ${option.label.toLowerCase()}`}
                        sx={{
                          height: 24,
                          fontSize: "0.7rem",
                          fontWeight: isActive ? 700 : 500,
                          border: "1px solid",
                          borderColor: isActive
                            ? darkMode
                              ? "rgba(255,255,255,0.35)"
                              : "rgba(0,0,0,0.2)"
                            : darkMode
                              ? "rgba(255,255,255,0.14)"
                              : "rgba(0,0,0,0.1)",
                          bgcolor: isActive
                            ? darkMode
                              ? "rgba(255,255,255,0.12)"
                              : "rgba(0,0,0,0.08)"
                            : "transparent",
                        }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            }
          />

          <CardContent
            sx={{
              flex: 1,
              minHeight: 0,
              mt: -4,
              overflowY: "auto",
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari
              },
            }}
          >
            {loading ? (
              <Stack gap={1}>
                <Skeleton variant="rounded" height={28} />
                <Skeleton variant="rounded" height={28} />
                <Skeleton variant="rounded" height={28} />
                <Skeleton variant="rounded" height={28} />
                <Skeleton variant="rounded" height={28} />
              </Stack>
            ) : (
              <RankedList
                items={attentionItems}
                emptyText={
                  numClients
                    ? attentionFilter === "all"
                      ? "Everyone looks good right now."
                      : `No ${attentionFilter} clients right now.`
                    : "No clients yet."
                }
                onSelect={onSelectClient}
                selectedId={selectedClient?.userId ?? null}
                selectedClient={selectedClient}
                onNavigateToClient={onNavigateToClient}
                darkMode={darkMode}
              />
            )}
          </CardContent>
          <Box sx={{ height: 24 }} />
        </Box>

        {/* Middle SLIDE-IN COLUMN (scrollable; takes no space when closed) */}
        <Box
          sx={{
            width: {
              xs: sliderOpen ? "100%" : 0,
              sm: sliderOpen ? 320 : 0,
              md: sliderOpen ? 380 : 0,
              lg: sliderOpen ? 420 : 0,
            },
            transition: "width 300ms ease",
            overflow: "hidden",
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Content slides in and can scroll */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: "100%",
              overflowY: "auto", // ← makes the column scrollable
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": {
                display: "none", // Chrome, Safari
              },
              transform: `translateX(${sliderOpen ? "0px" : "-16px"})`,
              opacity: sliderOpen ? 1 : 0,
              transition: "transform 300ms ease, opacity 200ms ease",
              pr: 0.5,
            }}
          >
            {selectedClient ? (
              <ClientOverview
                u={{
                  userId: selectedClient.userId,
                  name: selectedClient.name,
                  email: selectedClient.email,
                  gender: selectedClient.gender,
                  height: selectedClient.height,
                  weight: selectedClient.weight,
                  birthday: selectedClient.birthday as any,
                  isMetricUnits: selectedClient.isMetricUnits,
                  workouts: selectedClient.workouts?.map((w) => ({
                    id: w.id,
                    dayOfExecution: w.dayOfExecution,
                    durationInMinutes: w.durationInMinutes,
                  })) as any,
                  meals: selectedClient.meals?.map((m) => ({
                    id: m.id,
                    dateTime: m.dateTime,
                    calories: m.calories,
                    protein: m.protein,
                    carbs: m.carbs,
                    fats: m.fats,
                    servings: m.servings,
                  })),
                  goals: selectedClient.goals,
                }}
              />
            ) : null}
          </Box>
        </Box>

        {/* RIGHT — MiniChat */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
            scrollbarWidth: "none", // Firefox
            "&::-webkit-scrollbar": {
              display: "none", // Chrome, Safari
            },
          }}
        >
          <MiniChat />
        </Box>
      </Box>
    </Box>
  );
}
